import constants from 'helper/constants';

class SDKBridgingManager {
  constructor(platform) {
    // SDK platform
    this.platform = platform;

    // sdkBridge for incoming sdk requests
    this.sdkBridge = {
      handleUserUpdateSuccess: null,
      handleUserUpdateFailure: null,
      handlePersonalizationSuccess: null,
      handlePersonalizationFailure: null,
      handleHearingTestPreparationSuccess: () => {},
    };

    // Attach sdkBridge to window
    window.sdkBridge = this.sdkBridge;
  }

  static hasPlatformInterface(platform) {
    switch (platform) {
      case constants.platform.ios:
        if (window.webkit !== undefined) {
          return true;
        }
        // eslint-disable-next-line no-console
        console.error('Initiating iOS platform sdk interface failed. Bridging object (window.webkit) is not defined.');
        return false;
      case constants.platform.android:
      case constants.platform.android_rom:
      case constants.platform.android_wht:
        if (typeof window.android !== 'undefined') {
          return true;
        }
        // eslint-disable-next-line no-console
        console.error('Initiating android platform sdk interface failed. Bridging object (android) is not defined.');
        return false;
      default:
        return false;
    }
  }

  static platformType(platform) {
    return constants.platformType[platform];
  }

  setPlatform(platform) {
    if (constants.platform[platform]) {
      this.platform = platform;
      this.platformType = SDKBridgingManager.platformType(platform);
      this.handleDebugMessage(`SDKBridgingManager configured for platform: ${platform}`);
    } else {
      this.handleDebugMessage('Error: Unknown SDK platform');
    }
  }

  applyPersonalization(callback) {
    const debugMessage = `Error: Could not apply personalization (platform: ${this.platform})`;

    this.sdkBridge.handlePersonalizationSuccess = (result) => {
      callback(constants.sdk.success, result);
    };
    this.sdkBridge.handlePersonalizationFailure = () => {
      callback(constants.sdk.failure);
    };

    const iOSBinding = () => {
      try {
        window.webkit.messageHandlers.mmApplyPersonalization.postMessage({});
        return true;
      } catch (err) {
        console.log(debugMessage); // eslint-disable-line no-console
        return false;
      }
    };
    const androidBinding = () => {
      if (window.android.applyPersonalization) {
        window.android.applyPersonalization(JSON.stringify({}));
        return true;
      }
      return false;
    };
    if (this.bindPlatform(this.platform, iOSBinding, androidBinding, debugMessage)) {
      return true;
    }
    return false;
  }

  exit() {
    const debugMessage = `Error: Could not exit webView (platform: ${this.platform})`;

    const iOSBinding = () => {
      try {
        window.webkit.messageHandlers.mmExit.postMessage({});
        return true;
      } catch (err) {
        console.log(debugMessage); // eslint-disable-line no-console
        return false;
      }
    };
    const androidBinding = () => {
      if (window.android.exit) {
        window.android.exit(JSON.stringify({}));
        return true;
      }
      return false;
    };

    if (this.bindPlatform(this.platform, iOSBinding, androidBinding, debugMessage)) {
      return true;
    }
    return false;
  }

  returnFromUserAccountPortal() {
    const debugMessage = 'Error: Could not notify SDK about user returning from account portal';

    const iOSBinding = () => {
      try {
        window.webkit.messageHandlers.mmReturnFromUserAccountPortal.postMessage({});
        return true;
      } catch (err) {
        console.log(debugMessage); // eslint-disable-line no-console
        return false;
      }
    };
    const androidBinding = () => {
      if (window.android.returnFromUserAccountPortal) {
        window.android.returnFromUserAccountPortal(JSON.stringify({}));
        return true;
      }
      return false;
    };

    return (this.bindPlatform(this.platform, iOSBinding, androidBinding.bind(this), debugMessage));
  }

  transferToken(token, callback = () => {}) {
    const debugMessage = `Error: Could not transfer token (platform: ${this.platform})`;

    this.sdkBridge.handleLoginSuccess = () => {
      callback(constants.sdk.success);
    };
    this.sdkBridge.handleLoginFailure = () => {
      callback(constants.sdk.failure);
    };

    const iOSBinding = () => {
      try {
        window.webkit.messageHandlers.mmUserLogin.postMessage({ token });
        return true;
      } catch (err) {
        console.log(debugMessage); // eslint-disable-line no-console
        return false;
      }
    };
    const androidBinding = () => {
      if (window.android.userLogin) {
        window.android.userLogin(JSON.stringify({ token }));
        return true;
      }
      return false;
    };

    if (this.bindPlatform(this.platform, iOSBinding, androidBinding, debugMessage)) {
      return true;
    }
    return false;
  }

  userUpdate(user, callback) {
    const debugMessage = `Error: Could not update user (platform: ${this.platform})`;

    this.sdkBridge.handleUserUpdateSuccess = () => {
      callback(constants.sdk.success);
    };

    this.sdkBridge.handleUserUpdateFailure = () => {
      callback(constants.sdk.failure);
    };

    const iOSBinding = () => {
      try {
        window.webkit.messageHandlers.mmUserUpdate.postMessage(user);
        return true;
      } catch (err) {
        console.log(debugMessage); // eslint-disable-line no-console
        return false;
      }
    };

    const androidBinding = () => {
      if (window.android.userUpdate) {
        window.android.userUpdate(JSON.stringify(user));
        return true;
      }
      console.log(debugMessage); // eslint-disable-line no-console
      return false;
    };

    if (this.bindPlatform(this.platform, iOSBinding, androidBinding, debugMessage)) {
      return true;
    }
    return false;
  }

  handleDebugMessage(message) {
    console.log(message); // eslint-disable-line no-console
    switch (this.platform) {
      case constants.platform.ios:
        if (SDKBridgingManager.hasPlatformInterface(this.platform)) {
          try {
            window.webkit.messageHandlers.mmDebugMessage.postMessage({ message });
          } catch (err) {
            console.log('Debug message could not be sent to SDK'); // eslint-disable-line no-console
          }
        }
        break;
      case constants.platform.android:
        if (SDKBridgingManager.hasPlatformInterface(this.platform)) {
          window.android.debugMessage(JSON.stringify({ message }));
        }
        break;
      default:
    }
  }

  bindPlatform(platform, iOSBinding, androidBinding, debugMessage) {
    switch (platform) {
      case constants.platform.ios:
        if (SDKBridgingManager.hasPlatformInterface(platform)) {
          return iOSBinding(platform);
        }
        return false;
      case constants.platform.android:
      case constants.platform.android_wht:
      case constants.platform.android_rom:
        if (SDKBridgingManager.hasPlatformInterface(platform)) {
          return androidBinding(platform);
        }
        return false;
      default:
        this.handleDebugMessage(debugMessage);
        return false;
    }
  }
}

export default SDKBridgingManager;



// WEBPACK FOOTER //
// ./src/helper/SDKBridgingManager.js